import type { PreviewSource } from '@open-file-viewer/core'
import type { ComputedRef, InjectionKey } from 'vue'
import { computed, inject, provide, shallowRef } from 'vue'

export interface FileViewerOpenOptions {
  file: PreviewSource
  fileName: string
  mimeType?: string
  title?: string
}

export interface FileViewerRequest extends FileViewerOpenOptions {
  requestId: number
  mimeType: string
}

export interface FileViewerContext {
  current: ComputedRef<FileViewerRequest | null>
  openFileViewer: (options: FileViewerOpenOptions) => void
  closeFileViewer: () => void
}

const fileViewerKey: InjectionKey<FileViewerContext> = Symbol('file-viewer')

export function provideFileViewer(): FileViewerContext {
  const currentRequest = shallowRef<FileViewerRequest | null>(null)
  let requestId = 0

  const context: FileViewerContext = {
    current: computed(() => currentRequest.value),
    openFileViewer(options) {
      currentRequest.value = {
        ...options,
        fileName: options.fileName.trim() || 'file',
        mimeType: options.mimeType?.trim() || '',
        requestId: ++requestId,
      }
    },
    closeFileViewer() {
      currentRequest.value = null
    },
  }

  provide(fileViewerKey, context)
  return context
}

export function useFileViewer(): FileViewerContext {
  const context = inject(fileViewerKey)
  if (!context) {
    throw new Error('useFileViewer must be used under the global Provider')
  }
  return context
}
